// Single source of truth for map marker categories and assets
// Keep icon paths RELATIVE and compose with the global baseUrl in components: `${baseUrl}${icon}`

export const MarkerCategory = Object.freeze({
  EMPLOYEE: 'employee',
  STATION: 'station',
  CAMPUS: 'campus',
  VEHICLE: 'vehicle',
  RING: 'ring',
});

// Central catalog: extend as needed. Colors/zIndex are optional metadata for maps/legends.
export const markerCatalog = {
  [MarkerCategory.EMPLOYEE]: {
    key: MarkerCategory.EMPLOYEE,
    labelKey: 'flexigo.legend.employee',
    icon: 'icons/markers/employee.svg',
    color: '#1976d2',
    zIndex: 30,
  },
  [MarkerCategory.STATION]: {
    key: MarkerCategory.STATION,
    labelKey: 'flexigo.legend.station',
    icon: 'icons/station.svg',
    hoverIcon: 'icons/station-dark.svg',
    editIcon: 'icons/station-darker.svg',
    color: '#2ea043',
    zIndex: 25,
  },
  [MarkerCategory.CAMPUS]: {
    key: MarkerCategory.CAMPUS,
    labelKey: 'flexigo.legend.campus',
    icon: 'icons/markers/campus.svg',
    color: '#d29922',
    zIndex: 35,
  },
  [MarkerCategory.VEHICLE]: {
    key: MarkerCategory.VEHICLE,
    labelKey: 'flexigo.legend.vehicle',
    icon: 'icons/markers/vehicle.svg',
    color: '#6f42c1',
    zIndex: 40,
  },
  [MarkerCategory.RING]: {
    key: MarkerCategory.RING,
    labelKey: 'flexigo.legend.ring',
    icon: 'icons/markers/ring.svg',
    color: '#e36209',
    zIndex: 20,
  },
};

export const categories = Object.freeze(Object.keys(markerCatalog));

export const getMarker = (key) => markerCatalog[key];

export const getIcon = (key, event = null) => {
  let iconKey = 'icon';
  if (event === 'hover') {
    iconKey = 'hoverIcon';
  }
  if (event === 'edit') {
    iconKey = 'editIcon';
  }
  const { [iconKey]: icon } = markerCatalog[key] || {};
  return icon;
};

// People marker categories used in legends and personnel markers
export const PeopleCategory = Object.freeze({
  WITHOUT_ROUTE: 'WITHOUT_ROUTE',
  WITHOUT_STATION: 'WITHOUT_STATION',
  BEST_COMMUTE_MODE_SHUTTLE: 'BEST_COMMUTE_MODE_SHUTTLE',
  DRIVERS_ON_ROUTE: 'DRIVERS_ON_ROUTE',
  DRIVER_CANDIDATES_WITH_NO_ROUTES: 'DRIVER_CANDIDATES_WITH_NO_ROUTES',
  IN_WALKING_DISTANCE: 'IN_WALKING_DISTANCE',
  OUT_OF_WALKING_DISTANCE: 'OUT_OF_WALKING_DISTANCE',
});

// Centralized catalog for personnel marker variants (icons live under web/public/icons)
export const peopleMarkerCatalog = {
  [MarkerCategory.STATION]: {
    key: MarkerCategory.STATION,
    legendName: 'stations',
    labelKey: 'flexigo.routeList.legend.showStations',
    icon: 'station',
    iconPath: 'icons/station.svg',
  },
  [PeopleCategory.IN_WALKING_DISTANCE]: {
    key: PeopleCategory.IN_WALKING_DISTANCE,
    legendName: 'assignedRoutes',
    labelKey: 'flexigo.routeList.legend.assignedRoutes',
    icon: 'oval-green',
    iconPath: 'icons/oval-green.svg',
  },
  [PeopleCategory.OUT_OF_WALKING_DISTANCE]: {
    key: PeopleCategory.OUT_OF_WALKING_DISTANCE,
    legendName: 'outOfWalkingDistance',
    labelKey: 'flexigo.routeList.legend.outOfWalkingDistance',
    icon: 'oval-red',
    iconPath: 'icons/oval-red.svg',
  },
  [PeopleCategory.WITHOUT_ROUTE]: {
    key: PeopleCategory.WITHOUT_ROUTE,
    legendName: 'withoutRoute',
    labelKey: 'flexigo.routeList.legend.withoutRoute',
    icon: 'oval-black-2',
    iconPath: 'icons/oval-black-2.svg',
  },
  [PeopleCategory.WITHOUT_STATION]: {
    key: PeopleCategory.WITHOUT_STATION,
    legendName: 'withoutStation',
    labelKey: 'flexigo.routeList.legend.withoutStation',
    icon: 'oval-orange',
    iconPath: 'icons/oval-orange.svg',
  },
  [PeopleCategory.DRIVERS_ON_ROUTE]: {
    key: PeopleCategory.DRIVERS_ON_ROUTE,
    legendName: 'drivers',
    labelKey: 'flexigo.routeList.legend.drivers',
    icon: 'oval-purple',
    iconPath: 'icons/oval-purple.svg',
  },
  [PeopleCategory.DRIVER_CANDIDATES_WITH_NO_ROUTES]: {
    key: PeopleCategory.DRIVER_CANDIDATES_WITH_NO_ROUTES,
    legendName: 'driverCandidates',
    labelKey: 'flexigo.routeList.legend.driverCandidates',
    icon: 'oval-purple-dotted',
    iconPath: 'icons/oval-purple-dotted.svg',
  },
  [PeopleCategory.BEST_COMMUTE_MODE_SHUTTLE]: {
    key: PeopleCategory.BEST_COMMUTE_MODE_SHUTTLE,
    legendName: 'bestCommuteShuttle',
    labelKey: 'flexigo.routeList.legend.bestCommuteShuttle',
    icon: null,
    iconPath: null,
  },
};

export const getPeopleMarker = (key) => peopleMarkerCatalog[key];

export const getPeopleIcon = (key) => {
  const { iconPath = '' } = peopleMarkerCatalog[key] || {};
  return iconPath;
};

export const getPeopleIconName = (key) => {
  const { icon = '' } = peopleMarkerCatalog[key] || {};
  return icon;
};

export const getPeopleCategoryByLegendName = (legendName) => {
  const found = Object.entries(peopleMarkerCatalog).find(([, v]) => v.legendName === legendName);
  return found ? found[0] : '';
};
